import type React from 'react';
import type {
  GetEntryManagementReturnValueType,
  StepCandidateType,
} from '../../../../../core/repositories/mypageEntryManagementsRepository';
import type {StepCreationTriggerType} from '../../../constants/entrySourceTypes';
import type {useAiTodoGeneration} from '../../../usecase/useAiTodoGeneration';
import type {useProgressDeleteModal} from '../../../usecase/useProgressDeleteModal';
import type {useProgressModalStates} from '../../../usecase/useProgressModalStates';
import {AiGeneratedTodosModal} from '../Presenter/AiGeneratedTodosModal';
import {DeleteModal} from '../Presenter/DeleteModal';
import {OverallStatusUpdateModal} from '../Presenter/Progress/OverallStatusUpdateModal';
import {AiTodoGenerateModalContainer} from './AiTodoGenerateModalContainer';
import {InputMethodSelectionModalContainer} from './InputMethodSelectionModalContainer';
import {ProgressDetailDrawer} from './ProgressDetailDrawer';
import {ProgressMgtCreateContainer} from './ProgressMgtCreateContainer';
import {
  ProgressStepDrawerContainer,
  type ProgressStepDrawerRef,
} from './ProgressStepDrawerContainer';
import {ReportAnalyzeModalContainer} from './ReportAnalyzeModalContainer';
import {ScreenshotUploadModalContainer} from './ScreenshotUploadModalContainer';
import {
  StepCandidatePreviewModalContainer,
  type StepCandidatePreviewModalRef,
} from './StepCandidatePreviewModalContainer';
import {TextImportModalContainer} from './TextImportModalContainer';

type Props = {
  // モーダル制御関連
  modals: ReturnType<typeof useProgressModalStates>['modals'];
  deleteModal: ReturnType<typeof useProgressDeleteModal>['deleteModal'];

  // 状態管理
  isAddingEntryManagement: boolean;
  selectedCompanyId: number | undefined;
  entryManagementId?: number;
  existingCompanyIds: number[];
  existingCompanyNames: string[];
  entryDetailData: GetEntryManagementReturnValueType | undefined;

  // Ref
  stepDrawerRef: React.RefObject<ProgressStepDrawerRef>;
  stepCandidatePreviewModalRef: React.RefObject<StepCandidatePreviewModalRef>;

  // ハンドラー - ステップ操作
  handleMethodSelected: (method: string) => void;
  handleSaveCandidate: (
    companyName: string | null,
    companyID: number | null,
    userEntryManagementID: number | undefined,
    candidate: StepCandidateType,
    entrySourceType: StepCreationTriggerType,
  ) => Promise<{stepID?: number; userEntryManagementID?: number} | undefined>;
  handleAddStep: () => void;

  // ハンドラー - エントリー操作
  handleEditEntry: () => void;
  handleDeleteEntry: (id: number) => void;
  handleConfirmDeleteEntry: () => Promise<void>;

  // ハンドラー - 削除
  handleOpenDeleteModal: ReturnType<
    typeof useProgressDeleteModal
  >['handleOpenDeleteModal'];
  handleConfirmDelete: ReturnType<
    typeof useProgressDeleteModal
  >['handleConfirmDelete'];

  // ハンドラー - ステータス更新
  onUpdateOverallStatus?: (status: string) => Promise<void>;
  handleOpenOverallStatusUpdateModal: () => void;

  // ハンドラー - レポート分析
  handleReportAnalyzeSuccess?: (result: {
    candidates: StepCandidateType[];
    companyID?: number | null;
    companyName?: string | null;
    userEntryManagementID?: number;
  }) => void;

  // AI機能
  aiTodoGeneration?: ReturnType<typeof useAiTodoGeneration>;

  // その他のデータ
  hasReportData?: boolean;
  nextStepName?: string;
  hasTodosForEntryManagement?: (entryManagementId: number) => boolean;

  // UI制御
  isSp: boolean;
  detailDrawerOpened: boolean;
  onCloseDetailDrawer: () => void;
};

/**
 * 選考管理に関連するオーバーレイ（Modal/Drawer）群を共通化したコンポーネント
 * 詳細ドロワー、各種モーダルを一元管理し、コンポーネント間のprops伝播を整理する
 */
export const ProgressOverlays: React.FC<Props> = ({
  // モーダル制御
  modals,
  deleteModal,

  // 状態管理
  isAddingEntryManagement,
  selectedCompanyId,
  entryManagementId,
  existingCompanyIds,
  existingCompanyNames,
  entryDetailData,

  // Ref
  stepDrawerRef,
  stepCandidatePreviewModalRef,

  // ハンドラー
  handleMethodSelected,
  handleSaveCandidate,
  handleAddStep,
  handleEditEntry,
  handleDeleteEntry,
  handleConfirmDeleteEntry,
  handleOpenDeleteModal,
  handleConfirmDelete,
  onUpdateOverallStatus,
  handleOpenOverallStatusUpdateModal,
  handleReportAnalyzeSuccess,

  // AI機能
  aiTodoGeneration,

  // その他のデータ
  hasReportData = false,
  nextStepName,
  hasTodosForEntryManagement,

  // UI制御
  isSp,
  detailDrawerOpened,
  onCloseDetailDrawer,
}) => {
  return (
    <>
      <ProgressDetailDrawer
        key={`progress-detail-${isSp ? 'sp' : 'pc'}-${entryManagementId ?? 0}`}
        entryManagementId={entryManagementId ?? 0}
        entryDetailData={entryDetailData}
        existingCompanyNames={existingCompanyNames}
        nextStepName={nextStepName}
        openInputMethodSelectionModal={handleAddStep}
        aiTodoGeneration={aiTodoGeneration}
        onEditStep={(stepData) => stepDrawerRef.current?.openEdit(stepData)}
        onEditEntryClick={handleEditEntry}
        onDeleteClick={() => {
          if (entryManagementId) {
            handleDeleteEntry(entryManagementId);
          }
        }}
        onOpenOverallStatusUpdateModal={handleOpenOverallStatusUpdateModal}
        onOpenReportAnalyzeModal={modals.reportAnalyzeModal.open}
        handleOpenDeleteModal={handleOpenDeleteModal}
        isSp={isSp}
        opened={detailDrawerOpened}
        onClose={onCloseDetailDrawer}
        hasTodosForEntryManagement={hasTodosForEntryManagement}
      />

      <InputMethodSelectionModalContainer
        opened={modals.inputMethodModal.opened}
        onClose={modals.inputMethodModal.close}
        onMethodSelected={handleMethodSelected}
        hasReportData={hasReportData}
        isAddingEntryManagement={isAddingEntryManagement}
      />

      <ScreenshotUploadModalContainer
        opened={modals.screenshotModal.opened}
        onClose={modals.screenshotModal.close}
        stepCandidatePreviewModalRef={stepCandidatePreviewModalRef}
        companyId={isAddingEntryManagement ? undefined : selectedCompanyId}
        entryManagementId={
          isAddingEntryManagement ? undefined : entryManagementId
        }
        onBack={modals.inputMethodModal.open}
      />

      <TextImportModalContainer
        opened={modals.textImportModal.opened}
        onClose={modals.textImportModal.close}
        stepCandidatePreviewModalRef={stepCandidatePreviewModalRef}
        companyId={isAddingEntryManagement ? undefined : selectedCompanyId}
        entryManagementId={
          isAddingEntryManagement ? undefined : entryManagementId
        }
        onBack={modals.inputMethodModal.open}
      />

      <StepCandidatePreviewModalContainer
        ref={stepCandidatePreviewModalRef}
        onSave={handleSaveCandidate}
      />

      <ProgressMgtCreateContainer
        opened={modals.addMgtModal.opened}
        onClose={modals.addMgtModal.close}
        existingCompanyIds={existingCompanyIds}
      />

      <ProgressStepDrawerContainer
        ref={stepDrawerRef}
        selectedEntryManagementId={entryManagementId ?? null}
        entryDetailData={entryDetailData?.ok ? entryDetailData.value : null}
        onBack={modals.inputMethodModal.open}
        onOpenChange={modals.stepModal.setIsOpen}
      />

      <DeleteModal
        opened={deleteModal.opened || modals.deleteEntryModal.opened}
        onClose={
          deleteModal.opened ? deleteModal.close : modals.deleteEntryModal.close
        }
        onDelete={
          deleteModal.opened ? handleConfirmDelete : handleConfirmDeleteEntry
        }
        title={
          modals.deleteEntryModal.opened
            ? 'この選考管理を削除しますか？'
            : undefined
        }
      />

      {aiTodoGeneration && (
        <>
          <AiTodoGenerateModalContainer
            opened={aiTodoGeneration.isSelectModalOpen}
            onClose={aiTodoGeneration.closeSelectModal}
            onGenerate={aiTodoGeneration.generateTodos}
            isGenerating={aiTodoGeneration.isGenerating}
            companyID={
              entryDetailData?.ok ? entryDetailData.value.companyID : null
            }
            companyName={
              entryDetailData?.ok
                ? entryDetailData.value.companyName
                : undefined
            }
            stepName={aiTodoGeneration.pendingStepName}
          />
          <AiGeneratedTodosModal
            opened={aiTodoGeneration.isModalOpen}
            onClose={aiTodoGeneration.closeModal}
            candidates={aiTodoGeneration.candidates}
            onSave={aiTodoGeneration.saveTodo}
            onSaveAll={aiTodoGeneration.saveAllTodos}
            isTodoSaved={aiTodoGeneration.isTodoSaved}
            isSaving={aiTodoGeneration.isSaving}
            isSavingAll={aiTodoGeneration.isSavingAll}
            savingIndex={aiTodoGeneration.savingIndex}
            editingIndex={aiTodoGeneration.editingIndex}
            editingCandidate={aiTodoGeneration.editingCandidate}
            onStartEdit={aiTodoGeneration.startEdit}
            onCancelEdit={aiTodoGeneration.cancelEdit}
            onUpdateCandidate={aiTodoGeneration.updateCandidate}
          />
        </>
      )}

      {entryDetailData?.ok && onUpdateOverallStatus && (
        <OverallStatusUpdateModal
          opened={modals.overallStatusUpdateModal.opened}
          onClose={modals.overallStatusUpdateModal.close}
          currentStatus={entryDetailData.value.overallStatus}
          onUpdateStatus={onUpdateOverallStatus}
        />
      )}

      {entryDetailData?.ok &&
        entryDetailData.value.companyID !== null &&
        entryDetailData.value.companyID !== 0 &&
        handleReportAnalyzeSuccess && (
          <ReportAnalyzeModalContainer
            opened={modals.reportAnalyzeModal.opened}
            onClose={modals.reportAnalyzeModal.close}
            companyID={entryDetailData.value.companyID}
            userEntryManagementID={entryManagementId}
            onSuccess={handleReportAnalyzeSuccess}
          />
        )}
    </>
  );
};
